/**
 * Pilot allocation budgets — the single source of truth for the three `base + level`
 * caps the reducer enforces (HASE, skill triggers, talents) and the per-key rank
 * ceiling. The reducer is the wall; the `/roster` UI mirrors these as affordances
 * (CLAUDE.md "UI mirrors the reducer's rules as affordances; it never re-enforces
 * them"). Both import from here so a cap can never drift between the gate and the
 * control — the same discipline as `mounts.ts`'s `weaponFitsSlot`/`slotSizesForMount`.
 */
import type { PilotData } from "./types";

/** Max rank per skill trigger / talent. Both share the 1–3 ceiling (0 = unowned). */
export const MAX_TRIGGER_RANK = 3;
/**
 * The flat bonus one *rank* of a skill trigger grants to that trigger's rolls.
 *
 * Rank and bonus are **different numbers** and the storage keeps rank (`PilotData.skills`
 * is `id → rank`, which is what the `4 + level` budget and the LL0 ceiling meter). RAW
 * Core p. 25: "At LL0, your pilot gets four triggers at +2 each"; p. 18 lists the LL0
 * build as "Four +2 pilot triggers", and levelling adds "+2 to an existing trigger or a
 * new trigger at +2" — so each rank is worth +2 and the ladder is I/II/III → +2/+4/+6
 * (the p. 28 sample pilot's "Read a situation (+6)" is the top of it). `lancer-data`'s
 * `rules.json` agrees: `"trigger_bonus_per_rank": 2`, `"max_trigger_rank": 3`.
 */
export const TRIGGER_BONUS_PER_RANK = 2;
/**
 * Σ(HASE points) budget base: total ≤ `HASE_BUDGET_BASE + level`.
 *
 * `2`, not `3` — every pilot gets two mech-skill points at LL0 and one more per level.
 * Three independent sources agree: the p.19 LEVEL CHART's TOTAL MECH SKILL POINTS column
 * (LL0 = 2 … LL12 = 14), p.35 prose ("At LL0, your character starts with two points to
 * spend on mech skills"), and `lancer-data`'s `rules.json` `"minimum_mech_skills": 2`.
 * The whole chart is transcribed and asserted in `budgets.levelChart.test.ts`.
 */
export const HASE_BUDGET_BASE = 2;
/** Σ(skill-trigger ranks) budget base: total ≤ `SKILL_BUDGET_BASE + level`. */
export const SKILL_BUDGET_BASE = 4;
/** Σ(talent ranks) budget base: total ≤ `TALENT_BUDGET_BASE + level`. */
export const TALENT_BUDGET_BASE = 3;

/** Σ of the ranks in a `Record<id, rank>` skill/talent allocation map. */
export function rankSum(map: Record<string, number>): number {
  let total = 0;
  for (const v of Object.values(map)) total += v;
  return total;
}

/** The highest rank held in a `Record<id, rank>` allocation map (0 when empty). */
export function maxHeldRank(map: Record<string, number>): number {
  let max = 0;
  for (const v of Object.values(map)) if (v > max) max = v;
  return max;
}

/**
 * The roll bonus a skill trigger held at `rank` grants — `rank × TRIGGER_BONUS_PER_RANK`,
 * i.e. rank I/II/III → +2/+4/+6, rank 0 (unowned) → +0.
 *
 * **Every surface that shows a trigger to a human must render this, never the raw rank** —
 * a rank-I trigger reads "+2" on a character sheet, not "+1". The reducer, the budget and
 * the inverse all key off *rank*, so this is a display-boundary mapping and lives here, in
 * the one place that owns the trigger numbers, rather than as a `* 2` at each call site.
 */
export function triggerBonus(rank: number): number {
  return rank * TRIGGER_BONUS_PER_RANK;
}

/**
 * The per-key rank ceiling at LL0: every talent and skill trigger sits at rank I.
 *
 * RAW p.18: an LL0 pilot picks *three distinct* rank-I talents and *four distinct* +2
 * triggers — the budget alone (3 talent ranks, 4 trigger ranks) can't say that, since it
 * would happily accept one rank-III talent. LL0 is the **only** level whose shape is
 * pinned this way: from LL1 up, reallocation ("Reallocate all ranks from one of your
 * talents to any other talent", p.18) makes a concentrated high rank legally reachable,
 * so there is no per-level ledger to build here — just this one floor.
 */
export const LL0_MAX_RANK = 1;

/**
 * The rank ceiling one talent / skill-trigger key may hold at `level`, given that key's
 * own maximum (`MAX_TRIGGER_RANK` for triggers; the talent's `ranks.length` for talents).
 * At LL0 this is `LL0_MAX_RANK`; above it, the key's own maximum. The reducer enforces
 * this as a wall, the editor mirrors it to disable the "+" — both read this one helper so
 * the gate and the control can't drift.
 */
export function rankCeilingAtLevel(level: number, keyMax: number): number {
  return level === 0 ? Math.min(LL0_MAX_RANK, keyMax) : keyMax;
}

/** Total HASE budget at this pilot's level (`HASE_BUDGET_BASE + level`). */
export function haseBudget(pilot: PilotData): number {
  return HASE_BUDGET_BASE + pilot.level;
}

/** Total skill-trigger rank budget at this pilot's level (`SKILL_BUDGET_BASE + level`). */
export function skillBudget(pilot: PilotData): number {
  return SKILL_BUDGET_BASE + pilot.level;
}

/** Total talent rank budget at this pilot's level (`TALENT_BUDGET_BASE + level`). */
export function talentBudget(pilot: PilotData): number {
  return TALENT_BUDGET_BASE + pilot.level;
}

/**
 * Core-bonus budget: a pilot earns one core bonus per 3rd LL (totals of 1/2/3/4 at
 * LL 3/6/9/12, RAW Core p. 34/116), so `floor(level / 3)`. The cap on the count of
 * taken core bonuses the reducer (`set_core_bonus`) enforces and the kit affordance
 * mirrors. (Distinct from the manufacturer-rank *prerequisite* in `licenses.ts` —
 * both gate a take.)
 */
export function coreBonusBudget(pilot: PilotData): number {
  return Math.floor(pilot.level / 3);
}
